/**
 * O que a IA fez com o trânsito, como itens da trilha da conferência (cronograma, tarefa 5).
 *
 * A trilha só tinha decisões e notas. Na fila de Conferência só há item pendente — sem
 * decisão, por definição —, então a trilha aparecia sempre vazia. Ela passa a começar pela
 * máquina: capturado, vistas agrupadas, placa lida, validação automática. Depois vêm as
 * pessoas.
 *
 * Só **conta** o que já está no detalhe. Nenhuma regra é decidida aqui.
 */
import { rotuloDaVista } from "../api/tipos";
import type { DetalheDoTransito } from "../api/tipos";
import { grupoDaClasse } from "../leituras";

export type ItemDaTrilha = {
  em: string;
  quem: string;
  titulo: string;
  mudancas: string[];
  texto: string | null;
  /** Feito pela máquina, não por uma pessoa: a tela o marca diferente. */
  ia: boolean;
};

function pct(v: number | null | undefined): string {
  if (v == null) return "";
  return ` (${Math.round(v <= 1 ? v * 100 : v)}%)`;
}

function maisRecente(datas: (string | null | undefined)[]): string | null {
  const validas = datas.filter((d): d is string => Boolean(d));
  return validas.length ? validas.reduce((a, b) => (a > b ? a : b)) : null;
}

export function itensDaIa(detalhe: DetalheDoTransito): ItemDaTrilha[] {
  const itens: ItemDaTrilha[] = [];
  const doUpload = detalhe.origem !== "PORTICO";
  const ia = (
    em: string,
    titulo: string,
    texto: string | null,
  ): ItemDaTrilha => ({
    em,
    quem: "IA",
    titulo,
    mudancas: [],
    texto,
    ia: true,
  });

  itens.push(
    ia(
      detalhe.timestamp_passagem,
      "Capturado",
      doUpload
        ? "por upload de imagens"
        : `${detalhe.praca}, faixa ${detalhe.faixa}`,
    ),
  );

  const vistas = detalhe.imagens_de_analise ?? [];
  const chegada =
    maisRecente(vistas.map((v) => v.criado_em)) ?? detalhe.timestamp_passagem;
  if (vistas.length > 1) {
    itens.push(
      ia(
        chegada,
        `${vistas.length} vistas agrupadas no mesmo veículo`,
        vistas.map((v) => rotuloDaVista(v.vista)).join(", "),
      ),
    );
  }

  const quando = detalhe.validacao_automatica_em ?? chegada;
  // A placa: a vista (ou câmera) mais confiante, e em quantas ela foi lida.
  const leituras = vistas.length
    ? vistas.map((v) => ({ placa: v.placa, confianca: v.placa_confianca }))
    : detalhe.leituras.map((l) => ({ placa: l.placa, confianca: l.confianca }));
  const lidas = leituras.filter((l) => l.placa);
  if (leituras.length > 0) {
    const melhor = lidas.reduce<(typeof lidas)[number] | null>(
      (m, l) => (!m || (l.confianca ?? 0) > (m.confianca ?? 0) ? l : m),
      null,
    );
    const onde = vistas.length ? "vista" : "câmera";
    itens.push(
      melhor
        ? ia(
            quando,
            `Placa lida: ${melhor.placa}${pct(melhor.confianca)}`,
            `em ${lidas.length} de ${leituras.length} ${onde}${leituras.length > 1 ? "s" : ""}`,
          )
        : ia(quando, "Placa não lida", `nenhuma ${onde} permitiu ler`),
    );
  }

  if (detalhe.validacao_automatica_em || vistas.length) {
    const grupo = grupoDaClasse(detalhe.silhueta);
    const partes = [
      grupo ? grupo.toLowerCase() : null,
      detalhe.eixos_detectados != null
        ? `${detalhe.eixos_detectados} eixos`
        : null,
      detalhe.categoria_sugerida != null
        ? `sugere categoria ${detalhe.categoria_sugerida}`
        : null,
      lidas.length > 1
        ? detalhe.divergencia
          ? "as vistas divergem na placa"
          : "as vistas concordam na placa"
        : null,
      detalhe.isento
        ? `indício de isenção${detalhe.enquadramento_isencao ? `: ${detalhe.enquadramento_isencao}` : ""}`
        : null,
    ].filter(Boolean);
    itens.push(
      ia(
        quando,
        "Validação automática",
        partes.length ? partes.join(" · ") : null,
      ),
    );
  }
  return itens;
}
